import React, { ReactNode, ReactElement, FC, useState, useEffect } from 'react';
import MainList, { MainListProps } from './MainList';
import { Switch, Route, withRouter, RouteComponentProps, RouteProps } from 'react-router-dom';
import Popper from '@material-ui/core/Popper';
import Checkbox from '@material-ui/core/Checkbox';
import Button from '@material-ui/core/Button';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import Paper from '@material-ui/core/Paper';
import IconButton from '@material-ui/core/IconButton';
import HeaderNavLinks, { ITabConfig } from '../Buttons/HeaderNavLinks';
import { createStyles, Theme, Fab, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { withStyles, WithStyles } from '@material-ui/styles';
import FilterListIcon from '@material-ui/icons/FilterList';
import CloseIcon from '@material-ui/icons/Close';
import AddIcon from '@material-ui/icons/Add';

export interface ListDashboardProps extends RouteComponentProps, WithStyles<typeof STYLES> {
    baseUrl: string
    itemList: Array<any>
    renderListItem: (item: any) => ReactElement
    onFilterSubmit?: () => void
    onPageChange?: (pageNumber: number) => void
    contentConfig?: Array<ITabConfig & { component: RouteProps['component'] }>
    onItemAddRequest?: () => void
    addButtonComponent?: ReactNode
    listProps: Omit<MainListProps<any>, 'classes' | 'list'>
    onItemSelected?: (itemId: any) => void
    onItemUnselected?: () => void
    filterConfig?: Array<{ name: string, isActive: boolean }>
    itemIdIndex?: number // It is the index of the id of the item in the url. By default 3
    filterComponent?: ReactNode
    totalItemsCount?: number
    onFilterChange?: (item: { name: string, isActive: boolean }) => void
    onFilterReset?: () => void
    itemId?: string;
    loadingSubDashboard?: boolean;
}

const SubDashboard = (props: ListDashboardProps) => {
    const { baseUrl, contentConfig = [], itemId = '' } = props;
    if (contentConfig.length < 1)
        return <div />;
    return (
        <>
            <HeaderNavLinks baseUrl={`${baseUrl}/${itemId}`} tabsConfig={contentConfig} />
            <Switch>
                {
                    contentConfig.map(link => {
                        return (
                            <Route key={link.link} path={`${baseUrl}/:id/` + link.link} component={link.component}></Route>
                        )
                    })
                }
            </Switch>
        </>
    )
}

const ListDashboard: FC<ListDashboardProps> = (props) => {

    const [selectedItemId, setSelectedItemId] = useState<string | undefined>(undefined);
    const [filterElementAnchor, setFilterElementAnchor] = useState<EventTarget & Element | null>(null);

    const handleFilterClick = (event: React.MouseEvent) => setFilterElementAnchor(filterElementAnchor ? null : event.currentTarget);

    const handleFilterMenuClose = () => setFilterElementAnchor(null);

    const handleFilterApllied = () => {
        if (typeof props.onFilterSubmit === 'function') {
            props.onFilterSubmit();
            setFilterElementAnchor(null);
        }
    }

    const selectItem = (props: ListDashboardProps) => {
        const { match, location, itemIdIndex = 3, onItemSelected, onItemUnselected } = props;

        const isExactUrl = match.isExact;
        if (isExactUrl) {
            setSelectedItemId(undefined);
            typeof onItemUnselected === 'function' && onItemUnselected();
            return; //Call action to remove item from reducer

        }
        const urlLocation = location.pathname;
        const itemId = urlLocation.split('/')[itemIdIndex];
        if (itemId) {
            setSelectedItemId(itemId);
            typeof onItemSelected === 'function' && onItemSelected(itemId);
        }
    }

    useEffect(() => {
        selectItem(props);
    });

    const handlePageChange = (pageNumber: number) => typeof props.onPageChange === 'function' && props.onPageChange(pageNumber);



    const { itemList, listProps, addButtonComponent, onItemAddRequest, filterComponent } = props;
    const list = itemList.map(props.renderListItem);
    const classes = useStyles();

    return (
        <>
            <MainList
                list={list}
                float={(selectedItemId) ? 'left' : 'center'}
                search={true}
                onPageChange={handlePageChange}
                totalItemsCount={props.totalItemsCount}
                {...listProps}
            >
                {
                    (props.onFilterSubmit && props.filterConfig) &&
                    ((filterComponent) ? filterComponent : (
                        <div className={classes.filterButtonContainer} >
                            <Fab className={classes.filterButton} onClick={handleFilterClick} >
                                <FilterListIcon />
                            </Fab>
                            <Popper
                                anchorEl={filterElementAnchor}
                                open={Boolean(filterElementAnchor)}
                            // onClose={handleFilterMenuClose}
                            >
                                <Paper className={clsx(classes.removeOutline, classes.paper)} >
                                    <FormGroup className={classes.formGroup} >
                                        {
                                            (props.filterConfig || []).map((item, i) => {
                                                return (
                                                    <FormControlLabel
                                                        key={i}
                                                        className={classes.checkbox}
                                                        control={
                                                            <Checkbox
                                                                checked={item.isActive}
                                                                onChange={() => typeof props.onFilterChange === 'function' ? props.onFilterChange(item) : undefined}
                                                                color="primary"
                                                            />
                                                        }
                                                        label={item.name}
                                                    />
                                                )
                                            })
                                        }
                                    </FormGroup>
                                    <div className={classes.buttonsContainer} >
                                        <Button className={classes.btn} color="primary" variant="contained" onClick={handleFilterApllied} >Apply</Button>
                                        <Button
                                            className={classes.btn}
                                            color="primary"
                                            variant="contained"
                                            onClick={
                                                () => {
                                                    typeof props.onFilterReset === 'function' && props.onFilterReset();
                                                    setFilterElementAnchor(null);
                                                }
                                            }
                                        >Reset</Button>
                                    </div>
                                </Paper>
                                <IconButton className={classes.crossButton} onClick={handleFilterMenuClose} >
                                    <CloseIcon />
                                </IconButton>
                            </Popper>
                        </div>
                    ))
                }
                {
                    (addButtonComponent) ? { addButtonComponent } : (
                        <Fab color="primary" onClick={onItemAddRequest}>
                            <AddIcon />
                        </Fab>
                    )
                }

            </MainList>
            {
                (selectedItemId) &&
                (
                    <div className={classes.content}>
                            <SubDashboard itemId={selectedItemId} {...props} />
                    </div>
                )

            }
            {props.children || <div />}
        </>
    )
}

const STYLES = (theme: Theme) => createStyles({
    content: {
        paddingLeft: 250,
        paddingBottom: 50,
        height: '100vh',
        overflow: 'auto',
        backgroundColor: theme.palette.background['default'],
        display: 'flex',
        flexDirection: 'column'
    },
    filterButtonContainer: {
        marginBottom: 5
    },
    filterButton: {
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.common.white
    },
    paper: {
        width: 200,
        borderRadius: 6,
        overflow: 'hidden',
        position: 'relative',
        paddingTop: 35
    },
    removeOutline: {
        outline: 0
    },
    buttonsContainer: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        marginTop: 15
    },
    btn: {
        flex: 1,
        borderRadius: 0
    },
    formGroup: {
        paddingLeft: 15,
    },
    checkbox: {
        height: 30,
    },
    crossButton: {
        position: 'absolute',
        right: 0, top: 0
    },
});

const useStyles = makeStyles(STYLES);

export default withStyles(STYLES)(withRouter(ListDashboard));
